// src/components/admin/marketplace/RevenueByCategory.tsx
// ATUALIZADO: combo + cycle = "Combos & Campos de Estudo", novo tipo access_all

import React, { useState } from 'react';
import { TrendingUp, Package, Users, Lightbulb } from 'lucide-react';
import './RevenueByCategory.css';
import { useRevenueByCategory } from '../../../hooks/useAdminQueries';

interface CategoryData {
    type: string;
    revenueCents: number;
    salesCount: number;
    uniqueProducts: number;
    averageTicketCents: number;
    percentageOfTotal: number;
}

interface OriginData {
    origin: string;
    revenueCents: number;
    salesCount: number;
    averageTicketCents: number;
    percentageOfTotal: number;
}

interface RevenueByCategoryProps {
    onShowToast: (message: string, type: 'success' | 'error' | 'info') => void;
}

const RevenueByCategory: React.FC<RevenueByCategoryProps> = ({ onShowToast: _onShowToast }) => {
    const [period, setPeriod] = useState('30');

    const { data, isLoading: loading } = useRevenueByCategory(period);

    const formatCurrency = (cents: number) => `R$ ${(cents / 100).toFixed(2)}`;

    const getCategoryColor = (type: string) => {
        const colors: Record<string, string> = {
            deck: '#3b82f6',
            // ✅ combo e cycle recebem a mesma cor — são a mesma categoria visualmente
            combo: '#8b5cf6',
            cycle: '#8b5cf6',
            access_all: '#003BD1',
        };
        return colors[type] || '#6b7280';
    };

    const getCategoryLabel = (type: string) => {
        const labels: Record<string, string> = {
            deck: 'Decks Individuais',
            combo: 'Combos & Campos de Estudo',
            cycle: 'Combos & Campos de Estudo', // mesmo label que combo
            access_all: 'Acesso Total',
        };
        return labels[type] || type;
    };

    const getOriginLabel = (origin: string) =>
        origin === 'vendor' ? 'Vendedores' : 'Vendas Diretas';

    // ✅ Mesclar combo + cycle em uma única entrada antes de renderizar
    const mergeCategories = (categories: CategoryData[]): CategoryData[] => {
        const merged: Record<string, CategoryData> = {};

        categories.forEach(cat => {
            // Normaliza: cycle vira combo
            const key = cat.type === 'cycle' ? 'combo' : cat.type;

            if (merged[key]) {
                // Já existe — somar os valores
                merged[key] = {
                    ...merged[key],
                    revenueCents: merged[key].revenueCents + cat.revenueCents,
                    salesCount: merged[key].salesCount + cat.salesCount,
                    uniqueProducts: merged[key].uniqueProducts + cat.uniqueProducts,
                    // Recalcular ticket médio ponderado
                    averageTicketCents: merged[key].salesCount + cat.salesCount > 0
                        ? Math.round(
                            (merged[key].revenueCents + cat.revenueCents) /
                            (merged[key].salesCount + cat.salesCount)
                        )
                        : 0,
                    percentageOfTotal: merged[key].percentageOfTotal + cat.percentageOfTotal,
                };
            } else {
                merged[key] = { ...cat, type: key };
            }
        });

        // Ordenar: access_all > combo > deck > custom_deck
        const order = ['access_all', 'combo', 'deck'];
        return Object.values(merged).sort(
            (a, b) => order.indexOf(a.type) - order.indexOf(b.type)
        );
    };

    if (loading) {
        return (
            <div className="rbc-loading">
                <div className="rbc-spinner"></div>
                <p>Carregando análise...</p>
            </div>
        );
    }

    if (!data) {
        return <div className="rbc-error">Erro ao carregar dados</div>;
    }

    const mergedCategories = mergeCategories(data.categories);
    console.log('categorias recebidas:', data.categories);
    console.log('após merge:', mergedCategories);

    return (
        <div className="rbc-container">
            {/* HEADER COM FILTRO */}
            <div className="rbc-header">
                <div>
                    <h2>Receita por Categoria</h2>
                    <p className="rbc-subtitle">Análise de receita por tipo de produto</p>
                </div>
                <div className="rbc-period-selector">
                    <span>Período:</span>
                    {['7', '30', '90', '365', 'all'].map((p) => (
                        <button
                            key={p}
                            className={period === p ? 'active' : ''}
                            onClick={() => setPeriod(p)}
                        >
                            {p === 'all' ? 'Tudo' : `${p}d`}
                        </button>
                    ))}
                </div>
            </div>

            {/* RECEITA TOTAL */}
            <div className="rbc-total-banner">
                <span className="rbc-total-label">Receita Total do Período</span>
                <span className="rbc-total-value">{formatCurrency(data.totalRevenueCents)}</span>
            </div>

            {/* INSIGHT */}
            {data.insight && (
                <div className="rbc-insight">
                    <Lightbulb size={18} />
                    <p>{data.insight}</p>
                </div>
            )}

            {/* CATEGORIAS — usando mergedCategories */}
            <div className="rbc-section">
                <h3><Package size={20} /> Receita por Tipo de Produto</h3>
                <div className="rbc-categories-grid">
                    {mergedCategories.map((category) => (
                        <div
                            key={category.type}
                            className="rbc-category-card"
                            style={{ borderLeftColor: getCategoryColor(category.type) }}
                        >
                            <div className="rbc-category-header">
                                <h4>{getCategoryLabel(category.type)}</h4>
                                <span
                                    className="rbc-category-badge"
                                    style={{ background: getCategoryColor(category.type) }}
                                >
                                    {category.percentageOfTotal.toFixed(1)}%
                                </span>
                            </div>
                            <div className="rbc-category-stats">
                                <div className="rbc-stat-item">
                                    <span className="rbc-stat-label">Receita</span>
                                    <span className="rbc-stat-value">{formatCurrency(category.revenueCents)}</span>
                                </div>
                                <div className="rbc-stat-item">
                                    <span className="rbc-stat-label">Vendas</span>
                                    <span className="rbc-stat-value">{category.salesCount}</span>
                                </div>
                                <div className="rbc-stat-item">
                                    <span className="rbc-stat-label">Produtos</span>
                                    <span className="rbc-stat-value">{category.uniqueProducts}</span>
                                </div>
                                <div className="rbc-stat-item">
                                    <span className="rbc-stat-label">Ticket Médio</span>
                                    <span className="rbc-stat-value">{formatCurrency(category.averageTicketCents)}</span>
                                </div>
                            </div>
                            <div className="rbc-category-progress">
                                <div
                                    className="rbc-progress-fill"
                                    style={{
                                        width: `${Math.min(category.percentageOfTotal, 100)}%`,
                                        background: getCategoryColor(category.type)
                                    }}
                                />
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            {/* ORIGEM */}
            <div className="rbc-section">
                <h3><Users size={20} /> Receita por Origem de Venda</h3>
                <div className="rbc-origins-grid">
                    {data.origins.map((origin: OriginData) => (
                        <div key={origin.origin} className="rbc-origin-card">
                            <div className="rbc-origin-header">
                                <h4>{getOriginLabel(origin.origin)}</h4>
                                <span className="rbc-origin-percentage">
                                    {origin.percentageOfTotal.toFixed(1)}%
                                </span>
                            </div>
                            <div className="rbc-origin-revenue">{formatCurrency(origin.revenueCents)}</div>
                            <div className="rbc-origin-details">
                                <div className="rbc-origin-detail">
                                    <span className="rbc-detail-label">Vendas</span>
                                    <span className="rbc-detail-value">{origin.salesCount}</span>
                                </div>
                                <div className="rbc-origin-detail">
                                    <span className="rbc-detail-label">Ticket Médio</span>
                                    <span className="rbc-detail-value">{formatCurrency(origin.averageTicketCents)}</span>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            {/* TABELA COMPARATIVA — também usa mergedCategories */}
            <div className="rbc-section">
                <h3><TrendingUp size={20} /> Comparação Detalhada</h3>
                <div className="rbc-table-wrapper">
                    <table className="rbc-table">
                        <thead>
                            <tr>
                                <th>Categoria</th>
                                <th>Receita</th>
                                <th>% Total</th>
                                <th>Vendas</th>
                                <th>Ticket Médio</th>
                                <th>Produtos</th>
                            </tr>
                        </thead>
                        <tbody>
                            {mergedCategories.map((category) => (
                                <tr key={category.type}>
                                    <td>
                                        <div className="rbc-category-name">
                                            <span
                                                className="rbc-category-dot"
                                                style={{ background: getCategoryColor(category.type) }}
                                            />
                                            {getCategoryLabel(category.type)}
                                        </div>
                                    </td>
                                    <td className="rbc-revenue-cell">{formatCurrency(category.revenueCents)}</td>
                                    <td>
                                        <span className="rbc-percentage-badge">
                                            {category.percentageOfTotal.toFixed(1)}%
                                        </span>
                                    </td>
                                    <td>{category.salesCount}</td>
                                    <td>{formatCurrency(category.averageTicketCents)}</td>
                                    <td>{category.uniqueProducts}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
};

export default RevenueByCategory;
